/**
 * ONE NAME, ONE NUMBER — the client half of D2 (#1203).
 *
 * `threads/cycle-1-believable/DECISIONS.md` §D2: the four "how much is
 * waiting" concepts get four names and four definitions BEFORE anything is
 * centralised, because centralising four things still called the same thing
 * produces one number that is wrong everywhere instead of four that disagree.
 *
 *   NEEDS YOU        open work assigned to me, actionable now. Server-side.
 *                    The nav "Tasks" badge, the "Needs you · N" chip, the
 *                    Tasks page heading and the field tray badge ALL read
 *                    this one number.
 *   WAITING ON OTHERS  blocked elsewhere. Counted, never summed into Needs you.
 *   SHARED INBOX     mail in boxes I can see, of which some are new to me.
 *                    A separate system; never added to Needs you.
 *   NOTIFICATIONS    the event feed behind the bell. A third system, with its
 *                    own unread count (`notification-bell.tsx`).
 *
 * THE RULE: no surface may add two of these together and label the result with
 * one of their names. `needsN + newMailN` rendered as "Notifications" broke
 * that three ways at once — it summed two concepts, wore a third's name, and
 * sat next to the bell that owns it.
 *
 * A failed read is not a zero. Every hook here reports `isError` and callers
 * hide the badge rather than print a confident "0" they cannot stand behind
 * (D4). Central 401 handling is #1196's job, not this module's.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { MailPeek } from "@/lib/inbox";

/** GET /api/tasks/counts — the four task-side numbers, computed server-side. */
export interface WorkCounts {
    /** Open, assigned to me, actionable now. */
    needsYou: number;
    /** Open and visible to me, blocked on someone else. */
    waitingOnOthers: number;
    /** Open and actionable in a queue I'm in, nobody has taken it. */
    unclaimed: number;
    /** Open, hidden until its snooze date. */
    snoozed: number;
    /**
     * Things I ASKED for that came back to me — handed back for my confirm,
     * or passed back (#1645). Its own chip ("2 back"); never summed into
     * Needs you.
     */
    backToYou: number;
}

export const EMPTY_WORK_COUNTS: WorkCounts = {
    needsYou: 0,
    waitingOnOthers: 0,
    unclaimed: 0,
    snoozed: 0,
    backToYou: 0,
};

export interface WorkCountsState {
    counts: WorkCounts;
    isLoading: boolean;
    /** True when the read failed. Callers must not render `counts` as fact. */
    isError: boolean;
}

/**
 * The one source for "how much work is on me". Keyed under `["tasks", …]` so
 * every existing `invalidateQueries({ queryKey: ["tasks"] })` after a done /
 * claim / pass / snooze refreshes the badges too.
 */
export function useWorkCounts(options: { enabled?: boolean } = {}): WorkCountsState {
    const q = useQuery({
        queryKey: ["tasks", "counts"],
        queryFn: () => api.get<WorkCounts>("/api/tasks/counts"),
        refetchInterval: 60_000,
        enabled: options.enabled ?? true,
    });
    return { counts: q.data ?? EMPTY_WORK_COUNTS, isLoading: q.isLoading, isError: q.isError };
}

export interface SharedInboxState {
    /** Threads open in the shared boxes I can see — the whole team's backlog. */
    total: number;
    /** Of those, the ones THIS person has never opened — the clearable half. */
    newToYou: number;
    peek: MailPeek | undefined;
    isLoading: boolean;
    isError: boolean;
}

/**
 * Shared inbox (email). Office-only — the shared box is an office surface, so
 * field devices never poll it. Its numbers stand beside Needs you and are
 * never folded into it.
 */
export function useSharedInbox(options: { enabled?: boolean } = {}): SharedInboxState {
    const q = useQuery({
        queryKey: ["mailbox", "inbox-peek"],
        queryFn: () => api.get<MailPeek>("/api/mailbox/inbox-peek"),
        refetchInterval: 60_000,
        enabled: options.enabled ?? true,
    });
    return {
        total: q.data?.count ?? 0,
        newToYou: q.data?.newCount ?? 0,
        peek: q.data,
        isLoading: q.isLoading,
        isError: q.isError,
    };
}

/**
 * "17 need you · 5 waiting on others · 3 unclaimed" — the Tasks page's
 * supporting line. Zero buckets are dropped, so the sentence only ever names
 * numbers that are actually on the screen, and the arithmetic closes against
 * the lanes below it.
 */
export function describeWorkCounts(counts: WorkCounts): string {
    const parts: string[] = [];
    parts.push(counts.needsYou === 1 ? "1 thing needs you" : `${counts.needsYou} things need you`);
    if (counts.waitingOnOthers > 0) parts.push(`${counts.waitingOnOthers} waiting on others`);
    if (counts.unclaimed > 0) parts.push(`${counts.unclaimed} unclaimed in your queues`);
    if (counts.snoozed > 0) parts.push(`${counts.snoozed} snoozed`);
    return parts.join(" · ");
}
